import { Affix, AffixProps, TabsProps } from 'antd';
import clx from 'classnames';
import React from 'react';
import { down } from 'styled-breakpoints';
import { useBreakpoint } from 'styled-breakpoints/react-styled';
import styled from 'styled-components';
import { AffixTabs } from './AffixTabs';
import { HeaderTitleBar } from './HeaderTitleBar';

const StyledPageHead = styled.div`
  position: relative;
  z-index: 9;

  display: flex;
  flex-direction: column;
  padding: 8px 16px;
  background-color: ${props => props.theme.colorBgContainer ?? '#fff'};
  box-shadow: ${props => props.theme.boxShadow};
  &.has-tabs {
    padding-bottom: 0;
  }
`;

/**
 * Interfaccia per i tabs dell'header - si basa su quella di antd,
 * in mancanza dell'export dell'interfaccia (TabItemType)
 * @see https://ant.design/components/tabs/
 */
export interface AffixTab {
  key: string;
  label: React.ReactNode;
  disabled?: boolean;
}

export interface AffixAdminHeaderProps extends Omit<AffixProps, 'children'> {
  // Titolo della pagina
  title: string | React.ReactNode;
  // Eventuale prefisso al titolo
  prefix?: React.ReactNode;
  // Briciole
  breadcrumb?: React.ReactNode;
  // proprietà delle tabs
  tabs?: TabsProps['items'];
  onTabChange?: (key: string) => void;
  tabBarExtraContent?: TabsProps['tabBarExtraContent'];
  onTabClick?: TabsProps['onTabClick'];
  // Area delle azioni
  actions?: React.ReactNode;
  // Funzione che ritorna true se l'elemento e affisso in testa alla pagina
  onAffixChange?: (affixed: boolean | undefined) => void;
  // Tab attiva
  activeTab?: string;
}
/**
 * Titolo della pagina, con breadcrumb, titolo, azioni e tabs.
 * Affisso alla testa della pagina allo scroll.
 */
export function AffixAdminHeader(props: AffixAdminHeaderProps) {
  const {
    tabs,
    onTabChange,
    tabBarExtraContent: extraTabs,
    onAffixChange,
    breadcrumb,
    title,
    prefix,
    actions,
    activeTab,
    onTabClick,
    ...affixProps
  } = props;
  const isMobile = useBreakpoint(down('md'));
  const [affixed, setAffixed] = React.useState<boolean | undefined>();

  const handleAffixChange = (affixed: boolean | undefined) => {
    onAffixChange?.(affixed);
    setAffixed(affixed);
  };

  const hasTabs = tabs && tabs.length > 0;
  return (
    <Affix onChange={affixed => handleAffixChange(affixed)} {...affixProps}>
      <StyledPageHead className={clx({ 'has-tabs': hasTabs })}>
        <HeaderTitleBar
          prefix={prefix}
          title={title}
          breadcrumb={breadcrumb}
          actions={actions}
          ellipsis={affixed}
        />
        {hasTabs && (
          <AffixTabs
            affixed={false}
            defaultActiveKey={tabs![0].key}
            activeKey={activeTab}
            items={tabs}
            onChange={onTabChange}
            tabBarExtraContent={extraTabs}
            onTabClick={onTabClick}
          />
        )}
      </StyledPageHead>
    </Affix>
  );
}
